<template>
  <div class="entry-container">
    <form action="/" class="content-entry">
      <div class="form-content">
        <h2>Create a Timestamp</h2>

        <template v-if="textEntry">
          <label class="text-entry">
            <span>Message text <a href="#" v-if="!timestampSubmitted" @click.prevent="switchBetweenTextEntry">Switch back to file select</a></span>
            <textarea
              ref="textField"
              spellcheck="false"
              placeholder="Type your message"
              v-model="textValue"
              :disabled="timestampSubmitted"
              @input="onTextInput"></textarea>
          </label>
        </template>

        <template v-else>
          <label class="upload-area" v-if="fileName == ''">
            <div class="upload-controls">
              <div class="upload-button-wrapper">
                <input type="file" @change="onFileChange" />
                <div class="file-button">Choose a File…</div>
              </div>
              <span class="drag-drop-hint">or drag and drop it here</span>
            </div>
            <p class="upload-disclaimer">File will not be uploaded</p>
          </label>
          <p class="text-switch" v-if="fileName == ''">
            Got a plain text message?
            <a href="#" @click.prevent="switchBetweenTextEntry">Switch to text</a>
          </p>

          <label v-if="fileName != ''">
            <span>Selected File</span>
            <div class="file-display">
              <IconFiletype :filename="fileName" />
              <div class="file-name">{{ fileName }}</div>
              <a href="#" class="close" v-if="!timestampSubmitted" @click.prevent="clearFile">
                <IconClose />
              </a>
            </div>
          </label>

        </template>

        <div class="form-action" v-if="fileName !== '' || (textEntry && textValue !== '')">
          <div class="hash-container">
            <HashDisplay :hash="hash" />
            <p v-if="fileName !== ''">This is the cryptographic signature of your file. Only this signature is published. File contents are never published.</p>
            <p v-else>This is the cryptographic signature of your message. Only this signature is published. Your message stays in your browser.</p>
          </div>

          <!-- <div class="encrypted-storage">
            <label>
              <input type="checkbox" name="storeEncrypted">
              <span>Save file to your encrypted storage</span>
            </label>
          </div> -->

          <div class="timestamp-actions" v-if="! timestampSubmitted">
            <button
              class="primary"
              :disabled="timestampSubmitted"
              @click.prevent="submitTimestamp">
              Timestamp
              <template v-if="fileName !== ''">file</template><template v-else>message</template>
            </button>
          </div>

          <div class="submitting" v-else-if="timestampSubmitting">
            <vue-loaders-ball-grid-pulse color="#001ddd" size="8px" />
          </div>

          <div class="submission-error" v-else-if="timestampSubmitted && timestampError">
            <Heading type="failure">
              Error timestamping {{ timestampType === 'file' ? 'file' : 'message' }}
            </Heading>
            <p>Something went wrong. Please try again later.</p>
          </div>

          <div class="submission-success" v-else>
            <Heading type="success">
              {{ timestampType === 'file' ? 'File' : 'Message' }} timestamped successfully
            </Heading>
            <p>Timestamp saved at {{ timestampResult.date }} UTC. The link below is its permanent URL. You will need this link to verify document integrity and download full cryptographic proof.</p>
            <p>
              <router-link :to="`/timestamp/${timestampResult.url}`" class="timestamp-link">
                {{ `${appURL}timestamp/${timestampResult.url}` }}
              </router-link>
            </p>

            <p>
              <a href="#" class="reset-link" @click.prevent="resetForm">
                I've saved the link. Clear and create a new timestamp.
              </a>
            </p>
          </div>

        </div>
      </div>
    </form>

    <!-- <TimestampSubmission
      v-if="timestampSubmitted"
      :submitting="timestampSubmitting"
      :type="timestampType"
      :submission="timestampResult"
      :error="timestampError" /> -->
  </div>
</template>

<script>
import IconFiletype from '@/components/icons/IconFiletype.vue'
import IconClose from '@/components/icons/IconClose.vue'
import Heading from '@/components/Heading.vue'
import HashDisplay from '@/components/HashDisplay.vue'

import moment from 'moment-mini'

function bufferHex (buffer) {
  return Array.from(buffer, function (byte) {
    return ('0' + (byte & 0xFF).toString(16)).slice(-2)
  }).join('')
}

function disableDragDrop () {
  const $filedrop = document.getElementsByTagName('html')[0]
  $filedrop.ondragover = null
  $filedrop.ondragenter = null
  $filedrop.ondragleave = null
  $filedrop.ondragend = null
  $filedrop.ondrop = null
}

var enableDragDrop = (_this) => {
  const $filedrop = document.getElementsByTagName('html')[0]

  $filedrop.ondragover = function () { $filedrop.className = 'hover'; return false }
  $filedrop.ondragenter = function () { $filedrop.className = 'hover'; return false }
  $filedrop.ondragleave = function () { $filedrop.className = ''; return false }
  $filedrop.ondragend = function () { $filedrop.className = ''; return false }

  $filedrop.ondrop = (e) => {
    e.preventDefault()
    $filedrop.className = ''
    const file = e.dataTransfer.files[0]
    _this.fileName = file.name
    _this.textEntry = false
    _this.hash = defaultText
    const reader = new FileReader()
    // TODO worker
    reader.onload = (event) => {
      window.crypto.subtle.digest({ name: 'SHA-256' }, event.target.result)
        .then((hashBuffer) => {
          const hashArray = new Uint8Array(hashBuffer)
          const hashString = bufferHex(hashArray)
          _this.hash = hashString
        })
    }
    reader.readAsArrayBuffer(file)
  }
}

const defaultText = 'Calculating...'

export default {
  name: 'CreateTimestamp',
  components: {
    IconFiletype,
    IconClose,
    Heading,
    HashDisplay
  },
  data: () => ({
    textEntry: false,
    textEntryTimeout: null,

    textValue: '',
    fileName: '',
    hash: '',

    timestampSubmitting: false,
    timestampSubmitted: false,
    timestampType: false,
    timestampResult: false,
    timestampError: false,

    appURL: process.env.VUE_APP_URL
  }),
  mounted () {
    enableDragDrop(this)
  },
  beforeDestroy () {
    disableDragDrop()
  },
  filters: {
    readableDate: function (date) {
      return moment(date).format('YYYY-MM-DD HH:mm')
    }
  },
  methods: {
    onTextInput () {
      if (this.textValue !== '') {
        const arrayBuffer = new TextEncoder().encode(this.textValue)
        window.crypto.subtle.digest({ name: 'SHA-256' }, arrayBuffer)
          .then((hashBuffer) => {
            const hashArray = new Uint8Array(hashBuffer)
            const hashString = bufferHex(hashArray)
            this.hash = hashString
          })
      } else {
        this.hash = ''
      }
    },
    onFileChange (e) {
      const files = e.target.files
      const file = files[0]
      this.fileName = file.name
      this.textValue = ''
      this.hash = defaultText
      const reader = new FileReader()
      reader.onload = (event) => {
        window.crypto.subtle.digest({ name: 'SHA-256' }, event.target.result)
          .then((hashBuffer) => {
            const hashArray = new Uint8Array(hashBuffer)
            const hashString = bufferHex(hashArray)
            this.hash = hashString
          })
      }
      reader.readAsArrayBuffer(file)
    },
    clearFile () {
      this.fileName = ''
    },

    submitTimestamp: async function () {
      this.timestampType = (this.textValue === '' ? 'file' : 'text')
      this.timestampSubmitted = true
      this.timestampSubmitting = true
      try {
        const body = {
          hash: this.hash
        }
        if (this.fileName) {
          body.filename = this.fileName
        }
        disableDragDrop()
        const rawResponse = await fetch(`${process.env.VUE_APP_API}timestamps`, {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })
        if (rawResponse.ok) {
          this.timestampResult = await rawResponse.json()
        } else {
          const json = await rawResponse.json()
          this.timestampError = json.error
        }
      } catch (error) {
        this.timestampError = error
      } finally {
        this.timestampSubmitting = false
      }
    },
    switchBetweenTextEntry () {
      this.textEntry = !this.textEntry
      if (this.textEntry) {
        this.$nextTick(() => {
          this.$refs.textField.focus()
          this.onTextInput()
        })
      }
    },
    resetForm () {
      this.textEntry = false
      this.textValue = ''
      this.fileName = ''
      this.hash = ''

      this.timestampSubmitting = false
      this.timestampSubmitted = false
      this.timestampType = false
      this.timestampResult = false
      this.timestampError = false

      enableDragDrop(this)
    }
  }
}
</script>

<style>
.switcher-container {
  text-align: center;
  margin-top: -36px;
}
.switcher {
  display: inline-flex;
  flex-direction: row;
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
  width: 480px;
  box-shadow: 0 0 1px rgba(64,73,133,0.6), 0 2px 6px rgba(64,73,133,0.3);
}
.switcher li {
  flex-grow: 1;
  flex-basis: 0;
}
.switcher li a {
  color: #535db4;
  display: block;
  padding: 8px 12px;
}
.switcher li.selected a {
  background: #535db4;
  color: #fff;
}
.switcher li a:hover {
  text-decoration: none;
}

.form-content {
  max-width: 560px;
  margin: 0 auto;
}
.form-content h2 {
  margin-top: 16px;
  color: #555;
  color: #525881;
  /* font-weight: normal; */
  font-size: 24px;
  /* text-transform: uppercase; */
  text-align: center;
  /* letter-spacing: 0.05em; */
}

form {
  margin-top: 60px;
  padding: 16px 16px 32px 16px;
}
form.content-entry {
  background: rgba(120, 134, 221, 0.1);
  border-radius: 8px
}
html.hover form.content-entry {
  background: rgba(120, 134, 221, 0.4);
}
label {
  display: block;
  margin-top: 16px;
}
label > span {
  display: block;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 8px;
  color: #666;
}
label > span a {
  float: right;
}
textarea {
  background: #fff;
  border-radius: 4px;
  width: 100%;
  border: 0;
  resize: vertical;
  font-size: 16px;
  line-height: 24px;
  padding: 16px;
  min-height: 120px;
  color: #333;
  display: block;
  box-shadow: 0 0 1px rgba(64,73,133,0.9);
}
textarea:focus {
  outline: none;
  box-shadow: 0 0 1px rgba(64,73,133,0.6), 0 2px 6px rgba(64,73,133,0.3);
}

.text-entry {
  margin: 32px 0 0 0;
}

.text-entry textarea:empty {
  margin-bottom: 8px;
}

.upload-area {
  text-align: center;
  margin: 32px 0;
}

.upload-controls {
  display: inline-flex;
  align-items: center;
}

.upload-button-wrapper {
  position: relative;
  overflow: hidden;
  display: block;
  width: 152px;
  height: 48px;
  margin-right: 16px;
  border-radius: 4px;
  box-shadow: 0 0 1px rgba(64,73,133,0.6), 0 2px 6px rgba(64,73,133,0.3);
  transition: box-shadow 0.3s;
}
.upload-button-wrapper:hover {
  box-shadow: 0 0 1px rgba(64,73,133,0.6), 0 3px 12px rgba(64,73,133,0.4);
}

.upload-disclaimer {
  font-size: 14px;
  line-height: 20px;
  color: #666;
  margin: 16px 0 0 0;
}

.file-button {
  color: #001ddd;
  background-color: #fff;
  padding: 12px 0;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
}
.upload-button-wrapper input[type=file] {
  font-size: 100px;
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  cursor: pointer;
}

.drag-drop-hint {
  display: none;
}

.text-switch {
  text-align: center;
  font-size: 14px;
  margin-bottom: 0;
}

.form-action {
  margin-top: 16px;
}

.hash-container {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
}
.hash-container > p {
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 0 16px;
}

.hash-action {
  display: flex;
  flex-direction: column;
}

.timestamp-actions {
  display: flex;
  justify-content: center;
}

.submitting {
  text-align: center;
}

.timestamp-link {
  font-weight: bold;
  font-size: 18px;
}

.reset-link {
  font-size: 14px;
  line-height: 16px;
  color: #666;
}

button {
  font-family: 'Work Sans', 'Roboto', sans-serif;
  cursor: pointer;
  font-size: 16px;
  line-height: 24px;
  background: #fff;
  color: #001ddd;
  border-radius: 4px;
  padding: 8px 16px;
  border: 0;
  box-shadow: 0 0 1px rgba(64,73,133,0.9);
}
button:disabled {
  box-shadow: 0 0 1px hsl(0, 0%, 75%);
  color: #999;
  cursor: default;
}
button.primary {
  background: #001ddd;
  color: #fff;
  box-shadow: none;
  font-weight: bold;
}

.timestamp-actions button {
  flex-grow: 1;
  flex-shrink: 0;
}

.timestamp-actions button.primary {
  flex-grow: 0;
  margin: 0;
}

button.primary[disabled] {
  background: #404040;
  cursor: default;
}

textarea[disabled] {
  background: #fff;
  box-shadow: none;
  color: #999;
}

.file-display {
  display: flex;
  background: #fff;
  box-shadow: 0 0 1px rgba(64,73,133,0.9);
  padding: 8px 8px 8px 12px;
  border-radius: 4px;
  align-items: center;
}

.file-name {
  margin: 0 16px 0 8px;
  flex-grow: 1;
  font-size: 16px;
  line-height: 24px;
  color: #333;
}

a.close {
  padding: 12px;
  display: block;
}

a.close svg {
  display: block;
}

@media screen and (min-width: 640px) {
  .drag-drop-hint {
    color: #555;
    display: block;
  }
  .timestamp-actions {
    flex-direction: row;
  }
  .timestamp-actions button {
    margin: 0 0 0 8px;
  }
}

</style>
